import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('uz-UZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

const STATUS = {
  pending:             { tKey: 'bulkStatusPending',    color: '#ffa500', bg: 'rgba(255,165,0,0.12)' },
  partially_activated: { tKey: 'bulkStatusPartialAct', color: '#27E0FF', bg: 'rgba(39,224,255,0.12)' },
  active:              { tKey: 'bulkStatusActive',     color: '#B6FF3C', bg: 'rgba(182,255,60,0.15)' },
  completed:           { tKey: 'bulkStatusCompleted',  color: '#8892a0', bg: 'rgba(255,255,255,0.04)' },
  expired:             { tKey: 'bulkStatusExpired',    color: '#FF3B4E', bg: 'rgba(255,59,78,0.12)' },
  partially_expired:   { tKey: 'bulkStatusPartialExp', color: '#FF3B4E', bg: 'rgba(255,59,78,0.08)' },
  cancelled:           { tKey: 'bulkStatusCancelled',  color: '#FF3B4E', bg: 'rgba(255,59,78,0.10)' },
};

const MODE = {
  shared_pool:    { tKey: 'bulkModeShared', color: '#27E0FF' },
  per_seat_phone: { tKey: 'bulkModePhone',  color: '#FF1E6E' },
};

const inp = {
  padding: '8px 12px', background: 'var(--card2)', border: '1px solid var(--border)',
  color: 'var(--text)', borderRadius: 8, fontSize: 13, outline: 'none',
};

export default function BulkBookings() {
  const { t } = useLang();
  const [groups,   setGroups]   = useState([]);
  const [stats,    setStats]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [labId,    setLabId]    = useState('');
  const [labs,     setLabs]     = useState([]);
  const [status,   setStatus]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const fmtMoney = val => `${Number(val || 0).toLocaleString('uz-UZ')} ${t('currency')}`;

  const today      = isoDate(new Date());
  const weekStart  = isoDate(new Date(Date.now() - 6 * 86400000));
  const monthStart = isoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const load = useCallback(async () => {
    setLoading(true);
    const gp = new URLSearchParams();
    const sp = new URLSearchParams();
    if (labId)    { gp.set('lab_id',    labId);    sp.set('lab_id',    labId); }
    if (status)     gp.set('status',    status);
    if (dateFrom) { gp.set('date_from', dateFrom); sp.set('date_from', dateFrom); }
    if (dateTo)   { gp.set('date_to',   dateTo);   sp.set('date_to',   dateTo); }

    const [gRes, sRes, lRes] = await Promise.allSettled([
      api.get(`/bookings/owner/groups${gp.toString() ? '?' + gp : ''}`),
      api.get(`/bookings/owner/stats${sp.toString()  ? '?' + sp : ''}`),
      api.get('/owner/labs'),
    ]);
    if (gRes.status === 'fulfilled') setGroups(gRes.value.data || []);
    if (sRes.status === 'fulfilled') setStats(sRes.value.data  || {});
    if (lRes.status === 'fulfilled') setLabs(lRes.value.data   || []);
    setLoading(false);
  }, [labId, status, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const toggle = id => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const hasDateFilter = dateFrom || dateTo;
  const activeCount   = (stats.active || 0) + (stats.partially_activated || 0);

  const quickFilters = [
    { label: t('today'),     from: today,      to: today },
    { label: t('thisWeek'),  from: weekStart,  to: today },
    { label: t('thisMonth'), from: monthStart, to: today },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>{t('bulkTitle')}</h1>
        <button onClick={load} style={{
          padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
          background: 'rgba(0,212,255,0.08)', border: '1px solid var(--cyan)', color: 'var(--cyan)',
        }}>{t('bulkRefresh')}</button>
      </div>

      {/* Statistika */}
      <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--text3)', letterSpacing: 0.5 }}>
        {hasDateFilter
          ? `📅 ${dateFrom || '...'} — ${dateTo || '...'} ${t('bulkDateRangeLabel')}`
          : t('bulkAllTime')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <StatCard label={t('bulkStatTotal')}       value={stats.total_groups ?? '—'} />
        <StatCard label={t('bulkStatusPending')}   value={stats.pending       ?? '—'} color="#ffa500" />
        <StatCard label={t('bulkStatusActive')}    value={activeCount || (stats.total_groups != null ? 0 : '—')} color="#B6FF3C" />
        <StatCard label={t('bulkStatusCompleted')} value={stats.completed     ?? '—'} color="#27E0FF" />
        <StatCard label={t('bulkStatusExpired')}   value={stats.expired       ?? '—'} color="#FF3B4E" />
        <StatCard label={t('bulkStatusCancelled')} value={stats.cancelled     ?? '—'} color="#FF3B4E" />
        <StatCard label={t('bulkStatEarned')}      value={fmtMoney(stats.earned)} color="#B6FF3C" />
        {!hasDateFilter && (
          <>
            <StatCard label={t('bulkStatTodayGroups')} value={stats.today_groups ?? '—'} color="#a78bfa" />
            <StatCard label={t('bulkStatTodayEarned')} value={fmtMoney(stats.today_earned)} color="#a78bfa" />
          </>
        )}
      </div>

      {/* Filterlar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={labId} onChange={e => setLabId(e.target.value)} style={{ ...inp }}>
          <option value="">{t('bulkAllClubs')}</option>
          {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>

        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp }}>
          <option value="">{t('bulkAllStatuses')}</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.tKey)}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ ...inp, width: 130 }} />
          <span style={{ color: 'var(--text3)', fontSize: 13 }}>—</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ ...inp, width: 130 }} />
        </div>

        {quickFilters.map(q => (
          <button key={q.label} onClick={() => { setDateFrom(q.from); setDateTo(q.to); }}
            style={{
              padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
              background: dateFrom === q.from && dateTo === q.to ? 'rgba(167,139,250,0.2)' : 'var(--card2)',
              border: `1px solid ${dateFrom === q.from && dateTo === q.to ? '#a78bfa' : 'var(--border)'}`,
              color: dateFrom === q.from && dateTo === q.to ? '#a78bfa' : 'var(--text2)',
            }}>{q.label}</button>
        ))}

        {hasDateFilter && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            style={{ padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
              background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#ef4444' }}>
            {t('bulkClearDate')}
          </button>
        )}
      </div>

      {/* Ro'yxat */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
      ) : groups.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>
          {t('bulkNone')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {groups.map(g => {
            const sm  = STATUS[g.status] || STATUS.pending;
            const md  = MODE[g.mode]     || {};
            const isOpen = expanded.has(g.id);
            return (
              <div key={g.id} style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 12, overflow: 'hidden',
              }}>
                <div onClick={() => toggle(g.id)} style={{
                  padding: '14px 18px', cursor: 'pointer',
                  display: 'flex', gap: 12, alignItems: 'center',
                }}>
                  <div style={{ minWidth: 36, fontWeight: 800, fontSize: 16, color: 'var(--cyan)', fontFamily: 'Orbitron,sans-serif' }}>
                    #{g.id}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 14 }}>{g.user_name || t('bulkUnknown')}</strong>
                      {g.user_phone && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{g.user_phone}</span>}
                      {md.tKey && <span style={pill(md.color)}>{t(md.tKey)}</span>}
                      <span style={pill(sm.color, sm.bg)}>{t(sm.tKey)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span>🏢 {g.lab_name}</span>
                      <span>📦 {g.package_name}</span>
                      <span>🖥 {g.total_seats} PC ({g.activated_seats}/{g.total_seats} {t('bulkSeatsActiveLabel')})</span>
                      <span style={{ color: '#B6FF3C', fontWeight: 600 }}>{fmtMoney(g.total_amount)}</span>
                      <span>📅 {fmtTime(g.scheduled_at)}</span>
                    </div>
                  </div>

                  <div style={{ color: 'var(--text3)', fontSize: 11 }}>{isOpen ? '▲' : '▼'}</div>
                </div>

                {isOpen && g.seats && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: 14, background: 'rgba(0,0,0,0.12)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, marginBottom: 10 }}>
                      {t('bulkSeatsLabel')} — {g.seats.length}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                      {g.seats.map(s => (
                        <div key={s.id} style={{
                          padding: '10px 12px', background: 'var(--card2)',
                          border: `1px solid ${s.activated_at ? '#B6FF3C44' : 'var(--border)'}`,
                          borderRadius: 8,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <strong style={{ fontSize: 13 }}>PC #{s.computer_number || s.computer_id}</strong>
                            <span style={pill(s.activated_at ? '#B6FF3C' : '#ffa500')}>
                              {s.activated_at ? t('bulkStatusActive') : (s.status ? t(STATUS[s.status]?.tKey || 'bulkStatusPending') : t('bulkStatusPending')).toUpperCase()}
                            </span>
                          </div>
                          {s.assigned_phone && (
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>📞 {s.assigned_phone}</div>
                          )}
                          {s.activated_at && (
                            <div style={{ fontSize: 10, color: '#B6FF3C', marginTop: 3 }}>
                              ✓ {fmtTime(s.activated_at)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)' }}>
          {t('bulkShowing')}: {groups.length}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      padding: '12px 14px', background: 'var(--card)',
      border: '1px solid var(--border)', borderRadius: 10,
    }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

function pill(color, bg) {
  return {
    padding: '2px 7px',
    background: bg || `${color}20`,
    color,
    border: `1px solid ${color}`,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };
}
